import { Skeleton } from "../../../../../shared/ui/Working";
import { useTranslation } from "../../i18n/useTranslation";
import Footer from "../../components/Footer";
import { ServiceFrame, SkeletonStepLead } from "./StepService";

/**
 * A FLOW STEP, BEFORE IT CAN DRAW (D4, Web Director 2026-10-07).
 *
 * Direct entry to /book/time, /details or /review (a reload, a shared link,
 * Back) waits on the profile and then the client identity before the step
 * knows what to show. Every wait a person can see longer than ~300 ms uses the
 * shared skeleton, never "Loading…" text over an empty page; the service step
 * has its own shape-matched skeleton (ServiceStepSkeleton), and this is the
 * generic step: the real header, the step bar and its rule, a heading block,
 * content blocks, and the pinned action bar, all with the real classes, so the
 * step landing fills them in rather than moving them.
 *
 * Every placeholder group is `aria-hidden` AND `inert`; the action bar holds
 * no button (a control that cannot work is a dead control). A screen reader
 * gets the visually hidden h1 and one polite "Loading…". Only the shared
 * header and footer are live, and both are the real ones the step renders.
 */
export function StepSkeleton({ testId }: { testId: string }) {
  const { t } = useTranslation();
  return (
    <ServiceFrame testId={testId} loading shape="step" lead={<SkeletonStepLead />}>

      <div className="b-scroll">
        <h1 className="b-visually-hidden">{t("profile.loading")}</h1>
        <div className="b-stack" aria-hidden="true" inert data-testid="step-skeleton">
          <Skeleton w="62%" h={36} />
          <Skeleton w="38%" h={20} />
          <Skeleton h={64} r={12} />
          <Skeleton h={64} r={12} />
          <Skeleton w="72%" h={20} />
        </div>
        <p className="b-visually-hidden" aria-live="polite">
          {t("profile.loading")}
        </p>
        <div className="b-stepmeta">
          <Footer as="div" />
        </div>
      </div>

      <div className="b-foot" aria-hidden="true" inert data-testid="skeleton-actions">
        <Skeleton h={50} r={10} />
      </div>
    </ServiceFrame>
  );
}

/**
 * THE TIME GRID, BEFORE ITS TIMES ARRIVE (D4). The date's heading, the "Pick a
 * time" line, then pills in the real `.b-slots` grid, so the times land where
 * their placeholders were. Only when nothing cached can be shown: cached
 * slots keep today's inert list instead (F02).
 */
export function TimeGridSkeleton() {
  const { t } = useTranslation();
  return (
    <>
      <section className="b3-time-group" aria-hidden="true" inert data-testid="slots-skeleton">
        {/* The date heading's and "Pick a time"'s line boxes (v3.css
            .b3-time-group h2/h3, line-height 1.5 on the text scale), so the
            pills sit exactly where the times land. */}
        <Skeleton w="52%" h="calc(17px * var(--bs, 1) * 1.5)" />
        <Skeleton w="24%" h="calc(13px * var(--bs, 1) * 1.5)" />
        <div className="b-slots">
          {[0, 1, 2, 3, 4, 5].map((pill) => (
            <Skeleton key={pill} h={48} r={12} />
          ))}
        </div>
      </section>
      <p className="b-visually-hidden" aria-live="polite">
        {t("profile.loading")}
      </p>
    </>
  );
}
